/* eslint-disable no-console */
/* eslint-disable linebreak-style */
import { EventEmitter } from 'events';

import { Device, DeviceInfo } from '@capacitor/device';
import { modalController, toastController } from '@ionic/core';
import { INotificationData } from 'bg-server/src/components/api/v1/push/notification/types';
import { IPushSubscription } from 'bg-server/src/components/api/v1/push/subscription/types';
import Translator from 'i18n-babel';

import { toastError } from 'src/helpers/errorHelpers';
import { detectBrowser, getOS, IBrowserInfo } from 'src/helpers/pwa';
import { IWebPushSubscription } from 'src/interfaces';

import { pushNotificationService } from './pushNotification';
import { ENV } from '../config/environments';
import { getOptions, httpRequest } from '../helpers/http';

/**
 * See: https://medium.com/@brankofuenzalida/entendiendo-el-plugin-push-notifications-de-capacitor-8ca84cdd8d38
 */
export class PushSubscriptionService {
    public notif: EventEmitter = new EventEmitter();
    public err: EventEmitter = new EventEmitter();

    public webPushToken: string;

    private device = Device;
    private deviceInfo: DeviceInfo;
    private browserInfo: IBrowserInfo = detectBrowser();
    private os : 'Mac OS' | 'iOS' | 'Windows' | 'Android' | 'Linux' | 'unknown' = getOS();
    private isInitialized = false;
    readonly VAPID_PUBLIC_KEY = ENV.vapidPublicKey;

    private getApiEndpoint(): string {
        const selectedCity = localStorage.getItem('selectedCity') || ENV.apiEndpoints[0].key;
        const apiEndpoint = ENV.apiEndpoints.find(endpoint => endpoint.key === selectedCity);
        return apiEndpoint ? `${apiEndpoint.url}/push/subscription` : `${ENV.apiEndpoints[0].url}/push/subscription`;
    }

    /**
     * If push notifications are enabled, it will initialize and subscribe to events.
     * If not initialized it will do nothing, that way we can safely call this function in app first start
     * and be sure that user will not be prompted to enable push notifications even if it is the first app opening.
     */
    async init(isReinitialize = false): Promise<void> {
        if (this.isInitialized && !isReinitialize) {
            return;
        }
        this.isInitialized = true;
        this.deviceInfo = this.deviceInfo || await Device.getInfo();

        if (this.isPushEnabled()) {
            await this.initPushService();
        }
    }

    /**
     * Enables or disables push notifications, checking if they are already enabled/disabled
     * @param isEnablePush whether if push should be enabled (true) or disabled (false)
     */
    enableDisable(isEnablePush = true): Promise<boolean> {
        const isPushEnabled = this.isPushEnabled();
        switch (true) {
            case isEnablePush && !isPushEnabled: return this.enable();
            case !isEnablePush && isPushEnabled: return this.disable();
            default:
                // !isEnablePush && !isPushEnabled
                // isEnablePush && isPushEnabled
                // Force to save, so initial case (never asked) is covered
                this.setPushEnabled(isPushEnabled);
                return Promise.resolve(isEnablePush);
        }
    }

    /**
    *
    * @returns true if isPushEnabled is not null on local storage, else false
    */
    isPushAskedToEnable() {
        return localStorage.getItem('isPushEnabled') !== null;
    }

    isPushEnabled() {
        return localStorage.getItem('isPushEnabled') === 'true' || false;
    }

    private setPushEnabled(isPushEnabled) {
        localStorage.setItem('isPushEnabled', JSON.stringify(isPushEnabled));
    }

    private saveToken(token) {
        localStorage.setItem('pushToken', token);
    }

    private getToken() {
        return localStorage.getItem('pushToken');
    }

    /**
     * Initializes push notifications:
     * - request permission first time
     * - create android channel
     * - starts listening for push notifications
     * - updates token to server
     */
    private async initPushService(): Promise<any> {
        // Si el navegador no soporta Service Workers o push notifications tiramos un error
        if (!('Notification' in window) || !('serviceWorker' in navigator)) {
            if (this.os === 'iOS' && this.browserInfo.name === 'Safari') {
                const modal = await modalController.create({
                    component: 'ios-safari-notification-instructions',
                    initialBreakpoint: 1,
                    breakpoints: [0, 1],
                    mode: 'ios',
                });
                await modal.present();
            } else {
                console.error('Su navegador/dispositivo no soporta notificaciones push o service workers');
                throw new Error(await Translator.t('Su dispositivo no soporta notificaciones push'));
            }
        }
        // Verificamos el permiso de notificaciones, si no esta definido lo pedimos, si esta negado tiramos un error
        const pushPermissionStatus = Notification.permission;
        if (pushPermissionStatus !== 'granted') {
            const permissionStatus = await Notification.requestPermission();
            if (permissionStatus !== 'granted') {
                // eslint-disable-next-line max-len
                console.error('Permiso denegado. Habilita las notificaciones en la configuración de tu navegador.');
                // eslint-disable-next-line max-len
                throw new Error(await Translator.t('Permiso denegado. Habilita las notificaciones en la configuración de tu navegador.'));
            }
        }

        // Escuchamos eventos de push que nos emitira el service worker
        const reg = await navigator.serviceWorker.ready;
        if (!reg) {
            console.error('Service worker not ready');
            throw new Error(await Translator.t('No se ha podido iniciar el servicio de notificaciones, intenta recargar la página'));
        } else {
            // Registramos los eventos que se llaman des del service worker
            navigator.serviceWorker.addEventListener('message', (event) => {
                if (event.data.type === 'PUSH_RECEIVED') {
                    console.log('PUSH_RECEIVED');
                    const payload: INotificationData = JSON.parse(event.data.data);
                    // Enviamos al backend que hemos recibido este push
                    pushNotificationService.confirmReceived(payload.custom.notId, this.webPushToken);
                    // Emitimos el evento de notificacion recibida para procesar la en el app-root
                    this.notif.emit('webnotification', payload);
                }
            });
            navigator.serviceWorker.addEventListener('message', (event) => {
                if (event.data.type === 'ACTION_PERFORMED') {
                    console.log('ACTION_PERFORMED');
                }
            });
        }

        // Obtenemos la suscripción actual
        const activeSubscription: PushSubscription | null = await reg.pushManager.getSubscription();
        if (activeSubscription) {
            const storedVapidKey = localStorage.getItem('vapidPublicKey');
            if ((storedVapidKey && storedVapidKey !== this.VAPID_PUBLIC_KEY) || !storedVapidKey) {
                // La VAPID key ha cambiado, desuscribimos para generar una nueva suscripción
                console.log('VAPID key changed, resubscribing...');
                await activeSubscription.unsubscribe();
            } else {
                console.log('Alredy subscribed');
                this.webPushToken = JSON.stringify(activeSubscription);
                await this.updateServer(JSON.stringify(activeSubscription));
                localStorage.setItem('vapidPublicKey', this.VAPID_PUBLIC_KEY);
                return JSON.stringify(activeSubscription);
            }
        }
        // Si no hay una suscripción activa (o fue eliminada por cambio de VAPID key), la creamos
        const subscription: IWebPushSubscription = await reg.pushManager.subscribe({ applicationServerKey: this.VAPID_PUBLIC_KEY, userVisibleOnly: true });
        this.webPushToken = JSON.stringify(subscription);
        await this.updateServer(JSON.stringify(subscription));
        localStorage.setItem('vapidPublicKey', this.VAPID_PUBLIC_KEY);
        return JSON.stringify(subscription);
    }

    private async updateServer(registrationId) {
        // isEnabled not needed as managed by server to true automatically
        const deviceInfo = await this.device.getInfo();
        const { identifier } = await this.device.getId();
        const data: IPushSubscription = {
            token: registrationId,
            uuid: identifier,
            deviceData: {
                manufacturer: deviceInfo.manufacturer,
                model: deviceInfo.model,
                platform: deviceInfo.platform,
                version: deviceInfo.osVersion,
            },
        };
        const token = localStorage.getItem('token');
        const response = await httpRequest(this.getApiEndpoint(), 'POST', data, token, getOptions(false));
        if (response.status >= 300) {
            throw new Error(response.statusText);
        }

        const result: IPushSubscription = await response.json();

        if (result) {
            this.setPushEnabled(result.isEnabled);
        }

        if (result) {
            this.setPushEnabled(result.isEnabled);
            this.saveToken(result.token);
        }
    }

    // Enable push notifications
    public async enable(): Promise<boolean> {
        const registrationId: string = await this.initPushService();
        await this.updateServer(registrationId);
        return true;
    }

    /**
     * Disable push notifications if they are not already disabled. When disabled, does nothing.
     * When enabled, makes a post request to `/push/subscription/unsubscribe` web service.
     * @param isForce force to disable push notifications even if they are already disabled (i.e. logout case)
     */
    public async disable(isForce = false): Promise<boolean> {
        if (this.isPushEnabled() || isForce) {
            const { identifier } = await this.device.getId();
            const data: IPushSubscription = {
                uuid: identifier,
                token: this.getToken(),
            };
            const token = localStorage.getItem('token');
            const response = await httpRequest(`${this.getApiEndpoint()}/unsubscribe`, 'POST', data, token);
            if (response.status >= 300) {
                throw new Error(response.statusText);
            }
            const result: IPushSubscription = await response.json();
            if (result) {
                this.setPushEnabled(result.isEnabled);
            }
        } else {
            this.setPushEnabled(false);
        }
        return false;
    }
}

export const pushSubscriptionService: PushSubscriptionService = new PushSubscriptionService();

export async function initNotifications() {
    try {
        await pushSubscriptionService.init();
    } catch (err) {
        (await toastController.create({ message: err, duration: 3000 })).present();
    }

    if (!pushSubscriptionService.isPushAskedToEnable()) {
        const modal = await modalController.create({
            component: 'alert-modal',
            cssClass: 'auto-height alert-modal',
            componentProps: {
                header: await Translator.t('Vols rebre notificacions emergents?'),
                message: await Translator.t('Si dius que sí, rebràs notificacions sobre novetats o avisos importants'),
                buttons: [
                    {
                        text: 'Si',
                        role: 'accept',
                        handler: async () => {
                            try {
                                await pushSubscriptionService.enable();
                            } catch (e) {
                                console.error(e);
                                await toastError(`${await Translator.t('No hem pogut activar les notificacions')}: ${await Translator.t(e)}`);
                            }
                        },
                    },
                    {
                        text: 'No',
                        handler: async () => {
                            try {
                                await pushSubscriptionService.disable();
                            } catch (e) {
                                console.error(e);
                                await toastError(`${await Translator.t('No hem pogut desactivar les notificacions')}`);
                            }
                        },
                    },
                ],
            },
        });
        await modal.present();
    }
}
